import React from 'react';
import {
  VMK_PROFILE_NAME,
  getLetterLabel,
  buildSpecRows,
  getItemHeaderSubtitle,
  formatMm,
  formatSqm,
  formatMeters,
  formatVmkUnitPrice,
  sqmDisplayFromMmDims,
  isKeralitQuote,
  isPlastisolQuote,
  isDakkapelQuote,
  getKeralitProfileName,
  getPlastisolProfileName,
  shouldShowNijBegunMaatregelcode,
  getNijBegunMaatregelcode,
  svgLooksLikeKeralit,
  svgLooksLikePlastisol,
  svgLooksLikeDakkapel,
} from '../lib/vmkQuoteTable';
import { calcKeralitAreaSqm, getPlastisolMeters } from '../lib/pdfPricing';
import VmkQuoteDrawing from './VmkQuoteDrawing';

const VmkDimensionCell = ({ value }) => {
  if (!value) return '\u00a0';
  const mmMatch = String(value).match(/^(\d+)(mm)$/i);
  if (mmMatch) {
    return (
      <>
        <span className="vmk-dim-num">{mmMatch[1]}</span>
        <span className="vmk-dim-unit">mm</span>
      </>
    );
  }
  const sqmMatch = String(value).match(/^([\d.,]+)\s*(m²)$/i);
  if (sqmMatch) {
    return (
      <>
        <span className="vmk-dim-num">{sqmMatch[1]}</span>
        <span className="vmk-dim-unit"> {sqmMatch[2]}</span>
      </>
    );
  }
  const mMatch = String(value).match(/^([\d.,]+)\s*(m)$/i);
  if (mMatch) {
    return (
      <>
        <span className="vmk-dim-num">{mMatch[1]}</span>
        <span className="vmk-dim-unit"> {mMatch[2]}</span>
      </>
    );
  }
  return value;
};

const VmkQuoteLineItem = ({
  quote,
  index,
  svgHtml,
  lineTotal,
  formatPrice,
  drawingLoadingLabel,
  onRemove,
  showRemove,
  removeLabel,
  compact = false,
  showPricing = true,
  showNijBegun = false,
}) => {
  const config = quote.config || {};
  const quantity = config.quantity ?? 1;
  const unitPrice = quantity > 0 ? lineTotal / quantity : lineTotal;
  const letter = getLetterLabel(index);
  const subtitle = getItemHeaderSubtitle(config, quote.type);
  const isKeralit = isKeralitQuote(quote, quote.type) || svgLooksLikeKeralit(svgHtml);
  const isPlastisol =
    isPlastisolQuote(quote, quote.type) || svgLooksLikePlastisol(svgHtml);
  const isDakkapel =
    isDakkapelQuote(quote, quote.type) || svgLooksLikeDakkapel(svgHtml);
  // Area products (Keralit / Plastisol): no W×H mm on drawing or profile row — only m²
  const isAreaCladding = isKeralit || isPlastisol;
  const showMaatregelcode = showNijBegun && shouldShowNijBegunMaatregelcode(quote, svgHtml);
  const maatregelcode = showMaatregelcode ? getNijBegunMaatregelcode(quote) : null;
  const specRows = buildSpecRows(config, quote.type);
  const width = config.width || 0;
  const height = config.height || 0;
  const areaSqm = calcKeralitAreaSqm(width, height);
  const plastisolMeters = getPlastisolMeters(config);
  const profileLabel = isDakkapel
    ? 'Dakkapel'
    : isPlastisol
    ? getPlastisolProfileName(config.profileSystem)
    : isKeralit
      ? getKeralitProfileName(config.profileSystem)
      : subtitle
        ? `${VMK_PROFILE_NAME} ${subtitle.split(' - ')[0]}`
        : VMK_PROFILE_NAME;
  const profileDim1 = isPlastisol
    ? formatMeters(plastisolMeters)
    : isKeralit
      ? formatSqm(areaSqm)
      : formatMm(width);
  const profileDim2 = isAreaCladding ? '' : formatMm(height);

  return (
    <div className="vmk-line-item flex gap-3 print:break-inside-avoid">
      <div className="vmk-drawing flex-shrink-0 w-[36%]">
        {svgHtml ? (
          <VmkQuoteDrawing
            svgHtml={svgHtml}
            width={width}
            height={height}
            compact={compact}
            skipDimensionOverlays={isAreaCladding}
            hideHeightDimensionOverlay={isDakkapel}
          />
        ) : (
          <p className="text-gray-500 text-xs italic pt-4">{drawingLoadingLabel}</p>
        )}
      </div>

      <div className="vmk-spec-table flex-1 min-w-0">
        <table className="vmk-quote-table">
          <colgroup>
            <col className="vmk-c-col-index" />
            <col className="vmk-c-col-label" />
            <col className="vmk-c-col-desc" />
            <col className="vmk-c-col-dim" />
            <col className="vmk-c-col-dim" />
          </colgroup>
          <tbody>
            <tr>
              <td className="vmk-cell-center">{quantity}x</td>
              <td className="vmk-cell-center vmk-cell-letter">{letter}</td>
              <td colSpan={3} className="vmk-cell-subtitle">
                {subtitle || '\u00a0'}
              </td>
            </tr>

            <tr>
              <td className="vmk-cell-center vmk-cell-index" rowSpan={specRows.length + 1}>
                1
              </td>
              <td colSpan={2} className="vmk-cell-desc vmk-cell-profile-row">
                {profileLabel}
              </td>
              <td className="vmk-cell-dim"><VmkDimensionCell value={profileDim1} /></td>
              <td className="vmk-cell-dim"><VmkDimensionCell value={profileDim2} /></td>
            </tr>

            {specRows.map((row, rowIdx) => {
              const dims = sqmDisplayFromMmDims(row.dim1, row.dim2);
              const showSqm = Boolean(dims.dim1 && dims.dim1.includes('m²'));
              return (
                <tr key={rowIdx}>
                  <td colSpan={2} className="vmk-cell-desc vmk-cell-desc--spec">
                    <div>{row.description}</div>
                    {row.subline ? <div className="vmk-cell-subline">{row.subline}</div> : null}
                  </td>
                  {showSqm ? (
                    <td colSpan={2} className="vmk-cell-dim vmk-cell-dim--sqm">
                      <VmkDimensionCell value={dims.dim1} />
                    </td>
                  ) : (
                    <>
                      <td className="vmk-cell-dim"><VmkDimensionCell value={dims.dim1 || ''} /></td>
                      <td className="vmk-cell-dim"><VmkDimensionCell value={dims.dim2 || ''} /></td>
                    </>
                  )}
                </tr>
              );
            })}

            {showMaatregelcode ? (
              <tr className="vmk-nij-begun-row">
                <td colSpan={2} className="vmk-cell-desc vmk-nij-begun-label">
                  Maatregelcode Nij Begun
                </td>
                <td colSpan={3} className="vmk-cell-desc vmk-nij-begun-value">
                  {maatregelcode}
                </td>
              </tr>
            ) : null}

            {showPricing ? (
              <tr className="vmk-price-row vmk-yellow-row">
                <td colSpan={2} className="vmk-cell-price-label">
                  <span className="vmk-price-cell-inner vmk-price-cell-inner--label">Eenheidsprijs :</span>
                </td>
                <td colSpan={3} className="vmk-cell-price-value">
                  <span className="vmk-price-cell-inner vmk-price-cell-inner--value">
                    {formatVmkUnitPrice(unitPrice, quantity, lineTotal, formatPrice)}
                  </span>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {showRemove && onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="vmk-remove-btn mt-2 text-red-600 text-xs print:hidden pdf-export-hide hover:underline"
          >
            {removeLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default VmkQuoteLineItem;
